import React, { Dispatch, SetStateAction } from 'react';
import { useTable, useSortBy, usePagination } from 'react-table';
import { Table, Card, Input, Button, Alert } from 'reactstrap';
import styles from '../../styles/archiveui.module.css';
import SearchBar from './searchbar';
import { AuditEntry } from '../../types/audit';
import { User } from '../../types/user';
import { formatDateWithTime } from '../../utils/date';

interface AuditLogProps {
  audits: AuditEntry[];
  users: User[];
  exportAudits: () => void;
  updateActorFilter: Dispatch<SetStateAction<string>>;
  updateTargetFilter: Dispatch<SetStateAction<string>>;
  updateActionFilter: Dispatch<SetStateAction<string>>;
  updateStartDate: Dispatch<SetStateAction<string>>;
  updateEndDate: Dispatch<SetStateAction<string>>;
  actorFilter: string;
  targetFilter: string;
  actionFilter: string;
  startDate: string;
  endDate: string;
  searchAudits: () => void;
}

const AuditTable = ({
  audits,
  users,
  exportAudits,
  updateActorFilter,
  updateTargetFilter,
  updateActionFilter,
  updateStartDate,
  updateEndDate,
  actorFilter,
  targetFilter,
  actionFilter,
  startDate,
  endDate,
  searchAudits,
}: AuditLogProps): JSX.Element => {
  const columns: any = React.useMemo(
    () => [
      {
        Header: 'Document ID',
        accessor: 'target',
        // eslint-disable-next-line react/display-name
        Cell: ({ value }: { value: string }) => {
          if (!value) return <></>;
          // Legacy records stored the full API path (e.g. /v1/api/archive/{accountId}/document/{docId})
          // Extract only the document ID (last path segment)
          const lastSegment = value.split('/').filter(Boolean).at(-1) ?? value;
          return <>{lastSegment}</>;
        },
      },
      {
        Header: 'Time',
        accessor: 'time', // accessor is the "key" in the data
        minWidth: 140,
        // eslint-disable-next-line react/display-name
        Cell: (props: any) => {
          return <span style={{ whiteSpace: 'nowrap' }}>{formatDateWithTime(props.value)}</span>;
        },
      },
      {
        Header: 'account ID',
        accessor: 'accountId',
      },
      {
        Header: 'Application',
        accessor: 'application',
      },
      {
        Header: 'Actor',
        accessor: 'actor',
        // eslint-disable-next-line react/display-name
        Cell: ({ value }: { value: string }) => {
          const user = users.find((u) => u.cognitoIds?.includes(value));
          if (!user) return <>{value}</>;
          const name = [user.givenName, user.familyName].filter(Boolean).join(' ');
          return <>{name || user.email}</>;
        },
      },
      {
        Header: 'Source Type',
        accessor: 'sourceType',
      },
      {
        Header: 'Source',
        accessor: 'source',
      },
      {
        Header: 'Source IP',
        accessor: 'sourceip',
        // eslint-disable-next-line react/display-name
        Cell: ({ value }: { value: string }) => {
          if (!value) return <></>;
          return (
            <>
              {value.split(',').map((ip, index) => (
                <div key={index} style={{ whiteSpace: 'nowrap' }}>
                  {ip.trim()}
                </div>
              ))}
            </>
          );
        },
      },
      {
        Header: 'Action',
        accessor: 'action',
      },
      {
        Header: 'Description',
        accessor: 'description',
      },
      {
        Header: 'Result',
        accessor: 'result',
      },
      {
        Header: 'Meta',
        accessor: 'meta',
        // eslint-disable-next-line react/display-name
        Cell: ({ value }: { value: string }) => {
          if (!value) return <></>;
          let meta: any;
          try {
            meta = JSON.parse(value);
          } catch {
            return (
              <span
                style={{
                  fontSize: '0.7em',
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  color: '#555',
                }}
              >
                {value}
              </span>
            );
          }
          const detailsStyle = { minWidth: '450px' };
          const summaryStyle: React.CSSProperties = { cursor: 'pointer', fontSize: '0.75em', color: '#555' };
          const preStyle: React.CSSProperties = { margin: '4px 0 0', fontSize: '0.7em', whiteSpace: 'pre-wrap', wordBreak: 'break-word' };
          const prev = meta.prevObject;
          const updated = meta.updatedObject;
          if (!prev && !updated) {
            return (
              <details style={detailsStyle}>
                <summary style={summaryStyle}>view</summary>
                <pre style={preStyle}>{JSON.stringify(meta, null, 2)}</pre>
              </details>
            );
          }
          if (!prev || !updated) {
            const obj = prev || updated;
            return (
              <details style={detailsStyle}>
                <summary style={summaryStyle}>view</summary>
                <pre style={preStyle}>{JSON.stringify(obj, null, 2)}</pre>
              </details>
            );
          }
          const allKeys = Array.from(new Set([...Object.keys(prev), ...Object.keys(updated)]));
          const diffs = allKeys
            .filter((key) => JSON.stringify(prev[key]) !== JSON.stringify(updated[key]))
            .map((key) => ({ key, from: prev[key], to: updated[key] }));
          if (diffs.length === 0) return <span style={{ color: '#aaa', fontSize: '0.75em' }}>—</span>;
          const truncate = (v: any) => {
            const s = v === undefined ? 'undefined' : JSON.stringify(v) ?? 'null';
            return s.length > 40 ? s.slice(0, 40) + '…' : s;
          };
          return (
            <details style={detailsStyle}>
              <summary style={summaryStyle}>
                {diffs.length} change{diffs.length > 1 ? 's' : ''}
              </summary>
              <div style={{ fontFamily: 'monospace', fontSize: '0.7em', marginTop: '4px' }}>
                {diffs.map(({ key, from, to }) => (
                  <div key={key} style={{ marginBottom: '2px' }}>
                    <strong>{key}:</strong> <span style={{ color: '#c00' }}>{truncate(from)}</span>
                    {' → '}
                    <span style={{ color: '#080' }}>{truncate(to)}</span>
                  </div>
                ))}
              </div>
            </details>
          );
        },
      },
    ],
    [users],
  );

  const data = audits;

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable({ columns, data, initialState: { pageIndex: 0, pageSize: 25 } }, useSortBy, usePagination);

  return (
    <div>
      <SearchBar
        users={users}
        exportAudits={exportAudits}
        updateActorFilter={updateActorFilter}
        updateTargetFilter={updateTargetFilter}
        updateActionFilter={updateActionFilter}
        updateStartDate={updateStartDate}
        updateEndDate={updateEndDate}
        targetFilter={targetFilter}
        actionFilter={actionFilter}
        startDate={startDate}
        endDate={endDate}
        searchAudits={searchAudits}
      />
      {data.length > 0 ? (
        <>
          <div className={styles.tableContainer}>
            <Card style={{ marginTop: '1em', borderRadius: '.5em' }}>
              <div className={styles.scrollableTableWrapper}>
                <Table bordered striped hover size="sm" id="auditTable">
                  <thead className={styles.tableHeader}>
                    {headerGroups.map((headerGroup) => (
                      // eslint-disable-next-line react/jsx-key
                      <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column) => {
                          if (column.Header === 'Time') {
                            return (
                              // eslint-disable-next-line react/jsx-key
                              <th {...column.getHeaderProps(column.getSortByToggleProps())} className={`${styles.IdHeader} ${styles.stickyTh}`}>
                                {column.render('Header')}
                                {/* Add a sort direction indicator */}
                                <span>{column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}</span>
                              </th>
                            );
                          } else {
                            return (
                              // eslint-disable-next-line react/jsx-key
                              <th {...column.getHeaderProps(column.getSortByToggleProps())} className={`${styles.th} ${styles.stickyTh}`}>
                                {column.render('Header')}
                                {/* Add a sort direction indicator */}
                                <span>{column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}</span>
                              </th>
                            );
                          }
                        })}
                      </tr>
                    ))}
                  </thead>
                  <tbody {...getTableBodyProps()}>
                    {page.map((row, i) => {
                      prepareRow(row);
                      return (
                        // eslint-disable-next-line react/jsx-key
                        <tr {...row.getRowProps()}>
                          {row.cells.map((cell) => {
                            if (cell.column.Header === 'Time') {
                              return (
                                // eslint-disable-next-line react/jsx-key
                                <td style={{ paddingLeft: '1.25em' }} {...cell.getCellProps()}>
                                  {cell.render('Cell')}
                                </td>
                              );
                            } else {
                              return (
                                // eslint-disable-next-line react/jsx-key
                                <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                              );
                            }
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </Card>
          </div>
          <div className="pagination" style={{ justifyContent: 'end', marginTop: '.5em' }}>
            <div style={{ marginRight: '.5em' }}>
              <Button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
                {'<<'}
              </Button>{' '}
              <Button onClick={() => previousPage()} disabled={!canPreviousPage}>
                {'<'}
              </Button>{' '}
              <Button onClick={() => nextPage()} disabled={!canNextPage}>
                {'>'}
              </Button>{' '}
              <Button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
                {'>>'}
              </Button>{' '}
            </div>
            <div style={{ marginRight: '.5em' }}>
              <span>
                Page{' '}
                <strong>
                  {pageIndex + 1} of {pageOptions.length}
                </strong>{' '}
              </span>
            </div>
            <Input
              style={{ maxWidth: '10em' }}
              type="select"
              aria-label="Select Page Size"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
              }}
            >
              {[10, 20, 30, 40, 50].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}
            </Input>
          </div>
        </>
      ) : (
        <Alert color="warning" className={styles.noResultsAlert}>
          No results found...
        </Alert>
      )}
    </div>
  );
};

export default AuditTable;
